/******/ (() => { // webpackBootstrap
const formatWyrdSubmittedQuery = (query) => {
  const terms = query
    .trim()
    .split(/\s+/)
    .filter((term) => term && term.toUpperCase() !== 'AND');

  if (terms.join(' ').toLowerCase() === 'stl') {
    return 'tag:STL';
  }

  return terms.length > 1 ? terms.join(' AND ') : (terms[0] || '');
};

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    // DOM elements
    this.input = this.querySelector('[data-predictive-search-input]');
    this.submitButton = this.querySelector('[data-predictive-search-submit]');
    this.clearButton = this.querySelector('[data-predictive-search-clear]');
    this.resultsContainer = this.querySelector('[data-predictive-search-results]');
    this.emptyStateContainer = this.querySelector('[data-predictive-search-empty-state]');
    this.boxShadowElement = this.closest('.popup-shadow');

    // Initial state
    this.isPredictive = this.getAttribute('is-predictive') === 'true';
    this.searchQuery = this.input.value;
    this.searchChangeTimer = null;
    this.searchRequestController = null;
    this.searchRequestSequence = 0;
  
    // Bind event handlers
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleInputKeyDown = this.handleInputKeyDown.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.handleClearButtonVisibility = this.handleClearButtonVisibility.bind(this);
    this.handleNavButtonClick = this.handleNavButtonClick.bind(this);
    this.resetState = this.resetState.bind(this);
  }

  connectedCallback() {
    window.wetheme.webcomponentRegistry.register({key: 'component-predictive-search'});

    // Add event listeners
    this.input.addEventListener('input', this.handleInputChange);
    this.input.addEventListener('keydown', this.handleInputKeyDown);
    this.submitButton.addEventListener('click', this.handleSubmit);
    this.clearButton.addEventListener('click', this.handleClear);
  }

  handleInputChange() {
    this.handleClearButtonVisibility();

    if (this.searchRequestController) {
      this.searchRequestController.abort();
      this.searchRequestController = null;
    }
    this.searchRequestSequence += 1;

    if (!this.isPredictive) return;

    if (this.input.value !== '') {
      this.searchQuery = this.input.value;

      if (this.searchChangeTimer) {
        window.clearTimeout(this.searchChangeTimer);
      }

      this.searchChangeTimer = window.setTimeout(() => {
        this.performSearch(this.searchQuery);
        this.searchChangeTimer = null;
      }, 250);
    }
    else {
      this.resetState();
    }
  }

  handleClearButtonVisibility() {
    if (this.input.value === '') {
      this.clearButton.classList.add('fade-out');
      setTimeout(() => {
        this.clearButton.classList.add('hide');
        this.clearButton.classList.remove('fade-out');
      }, 250);
    }
    else {
      this.clearButton.classList.remove('hide');
    }
  }

  handleInputKeyDown(event) {
    if (event.key === 'Enter') {
      this.handleSubmit();
    }
  }

  handleSubmit() {
    const query = formatWyrdSubmittedQuery(this.input.value);
    if (!query) return;

    const params = new URLSearchParams({
      q: query,
      type: 'product',
      'options[prefix]': 'none',
      'options[unavailable_products]': 'show'
    });
    window.location.href = `${window.Shopify.routes.root}search?${params.toString()}`;
  }

  handleClear() {
    this.input.value = '';
    this.searchQuery = '';
    this.handleClearButtonVisibility();
    this.input.focus();
    this.resetState();
  }

  async performSearch(query) {
    const normalizedQuery = query.trim();
    if (!normalizedQuery) return;

    if (this.searchRequestController) {
      this.searchRequestController.abort();
    }

    const requestSequence = ++this.searchRequestSequence;
    const controller = new AbortController();
    this.searchRequestController = controller;

    const params = new URLSearchParams({
      q: normalizedQuery,
      section_id: 'predictive-search-results',
      'resources[type]': 'product',
      'resources[limit]': '10',
      'resources[limit_scope]': 'all',
      'resources[options][unavailable_products]': 'show',
      'resources[options][fields]': 'title,product_type,variants.title,variants.sku,variants.barcode,vendor,tag,body'
    });
    const url = `${window.Shopify.routes.root}search/suggest?${params.toString()}`;

    try {
      const response = await fetch(url, { signal: controller.signal });

      if (response.status === 417) {
        return;
      }

      if (!response.ok) {
        throw new Error(`Search request failed with status ${response.status}`);
      }

      const responseText = await response.text();
      const inputStillMatches = this.input.value.trim() === normalizedQuery;
      const requestIsCurrent = requestSequence === this.searchRequestSequence;

      if (!inputStillMatches || !requestIsCurrent) return;

      if (this.emptyStateContainer) {
        this.emptyStateContainer.setAttribute('hidden', '');
      }

      const doc = new DOMParser().parseFromString(responseText, 'text/html');
      const content = doc.querySelector('.shopify-section');
      if (content) {
        this.renderSearchResults(content.innerHTML);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Failed to fetch search results:', error);
      }
    } finally {
      if (requestSequence === this.searchRequestSequence) {
        this.searchRequestController = null;
      }
    }
  }
  renderSearchResults(html) {
    this.resultsContainer.innerHTML = html;

    // Remove any previously tracked event listeners
    if (this.boundNavButtons) {
      this.boundNavButtons.forEach(el => {
        el.removeEventListener('click', this.handleNavButtonClick);
      });
    }

    // Track elements with event listeners for later cleanup
    this.boundNavButtons = [];

    // Add nav button event listeners
    const navButtons = this.resultsContainer.querySelector('[data-predictive-search-nav-buttons]');
    if (navButtons) {
      navButtons.addEventListener('click', this.handleNavButtonClick);
      this.boundNavButtons.push(navButtons);
    }
  }

  handleNavButtonClick(e) {
    e.stopPropagation();
    const navButton = e.target;
    const resultId = navButton.getAttribute('data-nav-id');
    if (!resultId) return;

    const results = this.resultsContainer.querySelector(`[data-grid-id="${resultId}"]`);
    if (!results) return;

    const previousActiveButton = this.resultsContainer.querySelector(`[data-nav-id].active`);
    const previousActiveResult = this.resultsContainer.querySelector(`[data-grid-id]:not([hidden])`);

    // Set active nav button
    if (previousActiveButton) {
      previousActiveButton.setAttribute('aria-selected', 'false');
      previousActiveButton.classList.remove('active');
    }
    navButton.setAttribute('aria-selected', 'true');
    navButton.classList.add('active');

    // Set active results
    const heightContainer = this.resultsContainer.querySelector('[data-predictive-search-main-results]');
    const beforeHeight = heightContainer.clientHeight;

    if (previousActiveResult) {
      previousActiveResult.setAttribute('hidden', '');
    }
  
    results.removeAttribute('hidden');

    // Transition the height change
    heightContainer.animate([
      { height: `${beforeHeight}px` },
      { height: `${heightContainer.clientHeight}px` }
    ], { duration: 500, easing: 'cubic-bezier(0.85, 0, 0.15, 1)' });
  }

  resetState() {
    if (this.searchRequestController) {
      this.searchRequestController.abort();
      this.searchRequestController = null;
    }
    this.searchRequestSequence += 1;

    if (this.searchChangeTimer) {
      window.clearTimeout(this.searchChangeTimer);
    }

    if (this.emptyStateContainer) {
      this.emptyStateContainer.removeAttribute('hidden');
      this.renderSearchResults('');
      return;
    }

    this.resultsContainer.classList.add('fade-out');
    if (this.boxShadowElement) {
      this.boxShadowElement.classList.remove('popup-shadow');
    }

    setTimeout(() => {
      this.renderSearchResults('');
      this.resultsContainer.classList.remove('fade-out');
      if (this.boxShadowElement) {
        this.boxShadowElement.classList.add('popup-shadow');
      }
    }, 300);
  }

  disconnectedCallback() {
    if (this.searchRequestController) {
      this.searchRequestController.abort();
      this.searchRequestController = null;
    }

    // Remove event listeners
    this.input.removeEventListener('input', this.handleInputChange);
    this.input.removeEventListener('keydown', this.handleInputKeyDown);
    this.submitButton.removeEventListener('click', this.handleSubmit);
    this.clearButton.removeEventListener('click', this.handleClear);

    // Clean up dynamic listeners
    if (this.boundNavButtons) {
      this.boundNavButtons.forEach(el => {
        el.removeEventListener('click', this.handleNavButtonClick);
      });
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);

document.addEventListener('submit', (event) => {
  const form = event.target.closest?.('[data-wyrd-search-form]');
  if (!form) return;

  const input = form.querySelector('[data-wyrd-search-input]');
  if (!input) return;

  const query = formatWyrdSubmittedQuery(input.value);
  if (!query) {
    event.preventDefault();
    input.focus();
    return;
  }

  input.value = query;
});

/******/ })()
;
